import { useQuery } from "@tanstack/react-query";
import { fetchSalesEntryOffer } from "@/core/api/billingApi";
import {
  resolvePromoCode,
  resolveSalesEntryCode,
} from "@/shared/utils/salesEntry";

/**
 * SA-1218/SA-1279: a página de signup precisa saber se pode anunciar "N dias
 * grátis" e qual a claim do cupom.
 *
 * O client só tem os códigos OPACOS da URL, então quem traduz é o servidor
 * (`GET /billing/sales-entry-offer`, rota pública — aqui ainda não existe usuário
 * nem JWT). A resposta do link é um bit só: um link com cartão responde igual a
 * uma visita self-serve, então o modo continua opaco.
 *
 * `showTrialCopy` é o que as telas consomem, e ele começa **escondido** quando
 * há código: enquanto a resposta não chega, é melhor o selo aparecer um instante
 * depois do que prometer trial a quem vai ser cobrado no ato. Sem código nenhum
 * (fluxo self-serve) nada é consultado e a copy aparece de imediato, como sempre.
 *
 * Se a consulta falhar, cai no fail-safe do produto: mostra a copy padrão e
 * nenhuma claim de cupom (prometer desconto que o backend não confirmou seria o
 * erro simétrico, e mais caro).
 */
export function useSalesEntryOffer() {
  // Storage OU URL (ver `resolveSalesEntryCode`): numa visita fria o storage
  // ainda está vazio aqui, porque quem grava é um efeito do `AppProvider`. É a
  // mesma resolução que o cadastro usa para transportar o código, então a página
  // nunca anuncia uma oferta que o register não vai levar.
  const code = resolveSalesEntryCode();
  const promoCode = resolvePromoCode();

  const { data, isPending, isError } = useQuery({
    queryKey: ["billing", "sales-entry-offer", code, promoCode],
    queryFn: () => fetchSalesEntryOffer(code, promoCode),
    enabled: Boolean(code || promoCode),
    staleTime: Infinity,
    retry: false,
  });

  // A claim só aparece quando o servidor confirmou o cupom: código com typo ou
  // desligado no admin responde `promo: null` e a página não anuncia nada.
  const promoHeadline = data?.promo?.headline ?? null;

  if (!code) return { showTrialCopy: true, promoHeadline };
  if (isError) return { showTrialCopy: true, promoHeadline };
  if (isPending) return { showTrialCopy: false, promoHeadline };

  return { showTrialCopy: data?.has_trial !== false, promoHeadline };
}
